/**
 * WritingPractice Component
 *
 * Full writing practice flow: select prompt → write essay → receive AI assessment.
 * Integrates WritingPromptDisplay, WritingEditor, and WritingAssessmentResults.
 */

import React, { useState, useEffect } from 'react';
import { FileText, PenLine, ScrollText, Timer } from 'lucide-react';
import WritingPromptDisplay from './WritingPromptDisplay';
import WritingEditorSimple from './WritingEditorSimple';
import WritingAssessmentResults from './WritingAssessmentResults';
import selectiveApi from '../../../services/selectiveApi';
import './WritingPractice.css';

const WritingPractice = ({ onBack }) => {
  // Main flow states
  const [currentScreen, setCurrentScreen] = useState('select'); // 'select', 'writing', 'results', 'history'

  // Prompts data
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Assessment history
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    writing_type: 'all',
    tier: 'all',
    year_level: 'all'
  });

  // Writing state
  const [essayText, setEssayText] = useState('');
  const [writingStartTime, setWritingStartTime] = useState(null);

  // Assessment state
  const [assessment, setAssessment] = useState(null);
  const [assessing, setAssessing] = useState(false);
  const [assessmentError, setAssessmentError] = useState(null);

  // Fetch prompts on mount
  useEffect(() => {
    fetchPrompts();
  }, []);

  // Apply filters when prompts or filters change
  useEffect(() => {
    applyFilters();
  }, [prompts, filters]);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await selectiveApi.writing.getPrompts();
      // Backend returns { prompts: [], total: 0 }, extract the prompts array
      const promptsArray = data.prompts || data || [];
      setPrompts(promptsArray);
    } catch (err) {
      console.error('Failed to fetch prompts:', err);
      setError('Failed to load writing prompts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      setHistoryError(null);
      const data = await selectiveApi.writing.listAssessments(20);
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch assessment history:', err);
      setHistoryError('Failed to load your past assessments. Please try again.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleShowHistory = () => {
    setCurrentScreen('history');
    fetchHistory();
  };

  const handleOpenHistoryItem = async (assessmentId) => {
    try {
      setHistoryLoading(true);
      const full = await selectiveApi.writing.getAssessment(assessmentId);
      setAssessment(full);
      setCurrentScreen('results');
    } catch (err) {
      console.error('Failed to load assessment:', err);
      setHistoryError('Failed to open that assessment. Please try again.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...prompts];

    if (filters.writing_type !== 'all') {
      filtered = filtered.filter(p => p.writing_type === filters.writing_type);
    }

    if (filters.tier !== 'all') {
      filtered = filtered.filter(p => p.tier === filters.tier);
    }

    if (filters.year_level !== 'all') {
      filtered = filtered.filter(p => p.year_level === parseInt(filters.year_level));
    }

    setFilteredPrompts(filtered);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleStartWriting = (prompt) => {
    setSelectedPrompt(prompt);
    setEssayText('');
    setWritingStartTime(Date.now());
    setCurrentScreen('writing');
    setAssessmentError(null);
  };

  const handleBackToPrompts = () => {
    // Drafts are autosaved to localStorage, so leaving is safe
    setCurrentScreen('select');
    setSelectedPrompt(null);
    setEssayText('');
    setWritingStartTime(null);
  };

  const handleSubmitEssay = async (text) => {
    if (!text || text.trim().length === 0) {
      alert('Please write your essay before submitting.');
      return;
    }

    try {
      setAssessing(true);
      setAssessmentError(null);

      const timeTaken = writingStartTime
        ? Math.floor((Date.now() - writingStartTime) / 1000)
        : null;

      const assessmentData = await selectiveApi.writing.assess({
        prompt_id: selectedPrompt.id,
        writing_type: selectedPrompt.writing_type,
        essay_text: text,
        word_count: text.replace(/<[^>]*>/g, '').trim().split(/\s+/).length,
        time_taken_seconds: timeTaken
      });

      setAssessment(assessmentData);
      setCurrentScreen('results');
    } catch (err) {
      console.error('Assessment failed:', err);

      // Check for rate limit error
      if (err.message && err.message.includes('rate limit')) {
        setAssessmentError(
          'You have reached the assessment limit. Please try again later. ' +
          '(Limits: 3 per hour, 10 per day)'
        );
      } else {
        setAssessmentError(
          'Failed to assess your essay. Please try again or contact support if the problem persists.'
        );
      }
    } finally {
      setAssessing(false);
    }
  };

  const handleCloseResults = () => {
    setCurrentScreen('select');
    setSelectedPrompt(null);
    setEssayText('');
    setAssessment(null);
    setWritingStartTime(null);
  };

  // Get unique values for filters
  const getUniqueWritingTypes = () => {
    return [...new Set(prompts.map(p => p.writing_type))];
  };

  const getUniqueTiers = () => {
    return ['foundation', 'intermediate', 'advanced', 'mastery'].filter(tier =>
      prompts.some(p => p.tier === tier)
    );
  };

  const getUniqueYearLevels = () => {
    return [...new Set(prompts.map(p => p.year_level))].sort((a, b) => a - b);
  };

  return (
    <div className="writing-practice">
      {/* Header */}
      <div className="practice-header">
        {onBack && currentScreen === 'select' && (
          <button className="back-button" onClick={onBack}>
            ← Back to Dashboard
          </button>
        )}

        <div className="header-content">
          <h1 className="practice-title"><PenLine size={26} className="inline-icon" /> Writing Practice</h1>
          <p className="practice-subtitle">
            Practice your writing skills with AI-powered feedback
          </p>
        </div>

        {currentScreen === 'writing' && (
          <button className="back-button" onClick={handleBackToPrompts}>
            ← Back to Prompts
          </button>
        )}

        {currentScreen === 'select' && (
          <button className="back-button history-button" onClick={handleShowHistory}>
            <ScrollText size={16} className="inline-icon" /> My Past Assessments
          </button>
        )}

        {currentScreen === 'history' && (
          <button className="back-button" onClick={() => setCurrentScreen('select')}>
            ← Back to Prompts
          </button>
        )}
      </div>

      {/* Main Content Area */}
      <div className="practice-content">
        {/* Screen 1: Prompt Selection */}
        {currentScreen === 'select' && (
          <div className="prompt-selection">
            {/* Filters */}
            <div className="filters-panel">
              <h3 className="filters-title">Filter Prompts</h3>

              <div className="filter-group">
                <label className="filter-label">Writing Type</label>
                <select
                  className="filter-select"
                  value={filters.writing_type}
                  onChange={(e) => handleFilterChange('writing_type', e.target.value)}
                >
                  <option value="all">All Types</option>
                  {getUniqueWritingTypes().map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Tier</label>
                <select
                  className="filter-select"
                  value={filters.tier}
                  onChange={(e) => handleFilterChange('tier', e.target.value)}
                >
                  <option value="all">All Levels</option>
                  {getUniqueTiers().map(tier => (
                    <option key={tier} value={tier}>
                      {tier.charAt(0).toUpperCase() + tier.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Year Level</label>
                <select
                  className="filter-select"
                  value={filters.year_level}
                  onChange={(e) => handleFilterChange('year_level', e.target.value)}
                >
                  <option value="all">All Years</option>
                  {getUniqueYearLevels().map(year => (
                    <option key={year} value={year}>
                      Year {year}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-summary">
                Showing {filteredPrompts.length} of {prompts.length} prompts
              </div>
            </div>

            {/* Prompts List */}
            <div className="prompts-list">
              {loading && (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading prompts...</p>
                </div>
              )}

              {error && (
                <div className="error-state">
                  <p className="error-message">{error}</p>
                  <button className="retry-button" onClick={fetchPrompts}>
                    Try Again
                  </button>
                </div>
              )}

              {!loading && !error && filteredPrompts.length === 0 && (
                <div className="empty-state">
                  <p>No prompts match your filters. Try adjusting your selection.</p>
                </div>
              )}

              {!loading && !error && filteredPrompts.length > 0 && (
                <div className="prompts-grid">
                  {filteredPrompts.map(prompt => (
                    <div key={prompt.id} className="prompt-card">
                      <div className="prompt-card-header">
                        <span className={`type-badge type-${prompt.writing_type}`}>
                          {prompt.writing_type}
                        </span>
                        <span className={`tier-badge tier-${prompt.tier}`}>
                          {prompt.tier}
                        </span>
                      </div>

                      <div className="prompt-card-body">
                        <p className="prompt-preview">
                          {prompt.prompt_text.substring(0, 150)}
                          {prompt.prompt_text.length > 150 ? '...' : ''}
                        </p>
                      </div>

                      <div className="prompt-card-footer">
                        <div className="prompt-meta">
                          <span><FileText size={14} className="inline-icon" /> {prompt.min_words}-{prompt.max_words} words</span>
                          <span><Timer size={14} className="inline-icon" /> {prompt.time_limit_minutes} min</span>
                          <span>Year {prompt.year_level}</span>
                        </div>
                        <button
                          className="start-button"
                          onClick={() => handleStartWriting(prompt)}
                        >
                          Start Writing
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Screen 2: Writing Interface */}
        {currentScreen === 'writing' && selectedPrompt && (
          <div className="writing-interface">
            <WritingPromptDisplay
              prompt={selectedPrompt}
              showSuccessCriteria={true}
              compact={false}
            />

            {assessmentError && (
              <div className="assessment-error">
                <p>{assessmentError}</p>
                <button onClick={() => setAssessmentError(null)}>Dismiss</button>
              </div>
            )}

            <WritingEditorSimple
              initialValue={essayText}
              minWords={selectedPrompt.min_words}
              maxWords={selectedPrompt.max_words}
              timeLimit={selectedPrompt.time_limit_minutes * 60}
              onTextChange={setEssayText}
              onSubmit={handleSubmitEssay}
              disabled={assessing}
              autoSave={true}
              draftKey={selectedPrompt.id}
            />

            {assessing && (
              <div className="assessing-overlay">
                <div className="assessing-modal">
                  <div className="spinner large"></div>
                  <h3>Assessing Your Essay...</h3>
                  <p>Our AI is carefully reviewing your work. This may take 10-30 seconds.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Screen 3: Assessment Results */}
        {currentScreen === 'results' && assessment && (
          <div className="results-interface">
            <WritingAssessmentResults
              assessment={assessment}
              onClose={handleCloseResults}
            />
          </div>
        )}

        {/* Screen 4: Assessment History */}
        {currentScreen === 'history' && (
          <div className="history-interface">
            <h2 className="history-title">My Past Assessments</h2>

            {historyLoading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading your assessments...</p>
              </div>
            )}

            {historyError && (
              <div className="error-state">
                <p className="error-message">{historyError}</p>
                <button className="retry-button" onClick={fetchHistory}>
                  Try Again
                </button>
              </div>
            )}

            {!historyLoading && !historyError && history.length === 0 && (
              <div className="empty-state">
                <p>No assessments yet. Submit an essay to get AI feedback!</p>
              </div>
            )}

            {!historyLoading && !historyError && history.length > 0 && (
              <div className="history-list">
                {history.map(item => (
                  <button
                    key={item.id}
                    className="history-item"
                    onClick={() => handleOpenHistoryItem(item.id)}
                  >
                    <div className="history-item-main">
                      <span className={`type-badge type-${item.writing_type || 'narrative'}`}>
                        {item.writing_type || 'essay'}
                      </span>
                      <span className="history-band">{item.band_level || '—'}</span>
                      <span className="history-score">
                        {item.overall_score != null ? `${item.overall_score}/10` : ''}
                      </span>
                    </div>
                    <div className="history-item-meta">
                      <span>{item.word_count ? `${item.word_count} words` : ''}</span>
                      <span>
                        {item.assessed_at
                          ? new Date(item.assessed_at).toLocaleDateString('en-AU', {
                              day: 'numeric', month: 'short', year: 'numeric'
                            })
                          : ''}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WritingPractice;
